import './commitment-details-sheet.scss';

import type { ReactElement } from 'react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  LinkCard,
  LinkCardIcon,
} from '@sola/ark-core/molecules/link-card/link-card.tsx';
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetHeader,
  SheetTrigger,
} from '@sola/ark-core/molecules/sheet/sheet.tsx';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.tsx';
import type {
  GraphQLCommitment,
  GraphQLPledge,
} from '@sola/schema/dotcom/supporters/schema.generated.ts';
import { GraphQLCommitmentType } from '@sola/schema/dotcom/supporters/schema.generated.ts';
import {
  getCommitmentLabel,
  getCommitmentTitle,
  getFrequencyAbbr,
} from '@sola/supporter-sdk/utils/commitments.ts';

import { CommitmentAvatar } from '../../atoms/commitment-avatar/commitment-avatar.tsx';
import { PledgeIcon } from '../../atoms/pledge-icon/pledge-icon.tsx';
import {
  LocalAlertsProvider,
  useLocalAlerts,
} from '../../molecules/local-alerts/local-alerts-provider.tsx';
import { LocalAlert } from '../../molecules/local-alerts/local-alerts.tsx';
import useModalAlerts from '../../molecules/local-alerts/useModalAlerts.ts';
import { MakeOneTimePaymentAlert } from '../../molecules/make-one-time-payment-alert/make-one-time-payment-alert.tsx';
import { SectionContent } from '../../molecules/section-content/section-content.tsx';
import { formatPledgeName } from '../../utils/pledges.ts';
import { CommitmentDetailsPaymentPlan } from './commitment-details-payment-plan.tsx';

export interface CommitmentDetailsSheetProps {
  /**
   * This should only take in elements that resolve to html buttons.
   */
  isOpen?: boolean;
  onOpenChange?: (open: boolean, commitmentId: string) => void;
  openTrigger: ReactElement;
  commitment?: GraphQLCommitment | null;
  pledge?: GraphQLPledge | null;
}

export interface CommitmentDetailsProps {
  commitment: GraphQLCommitment;
}

export interface PledgeDetailsProps {
  pledge: GraphQLPledge;
}

interface CommitmentDetailsListItemProps {
  title: string;
  children: React.ReactNode;
}

export interface CommitmentDetailsSectionProps {
  title?: string;
  children: React.ReactNode;
}

interface CommitmentDetailsBodyProps {
  title: string;
  children: React.ReactNode;
}

const CommitmentDetailsListItem: React.FC<CommitmentDetailsListItemProps> = ({
  title,
  children,
}) => {
  return (
    <li className="label-md label-moderate label-neutral-mild">
      {title && (
        <span className="label-potent">
          {title}
          {': '}
        </span>
      )}
      <span>{children}</span>
    </li>
  );
};

export const CommitmentDetailsSection: React.FC<
  CommitmentDetailsSectionProps
> = ({ title, children }) => {
  return (
    <div className="CommitmentDetails__section py-comfy-xl">
      <SectionContent heading={title || ''}>
        <div className="flex flex-col gap-comfy-sm">{children}</div>
      </SectionContent>
    </div>
  );
};

export const CommitmentDetailsActionCard: React.FC = () => {
  const { t } = useTranslation(['my']);

  return (
    <div className="pt-comfy-xl">
      <LinkCard
        graphic={
          <LinkCardIcon>
            <ArkIcon iconName="message-question-square" size="18" />
          </LinkCardIcon>
        }
        title={t('my:commitments.details.needHelp.title')}
        subtitle={t('my:commitments.details.needHelp.description')}
        href={t('my:ctas.contactSupport.url')}
        staticPadding
      />
    </div>
  );
};

const CommitmentDetailsBody: React.FC<
  React.PropsWithChildren<CommitmentDetailsBodyProps>
> = ({ title, children }) => {
  return (
    <div className="CommitmentDetails flex flex-col">
      <h3 className="CommitmentDetails__title body-sm body-neutral-mild pb-comfy-xl">
        {title}
      </h3>
      {children}
    </div>
  );
};

export const PledgeDetails: React.FC<PledgeDetailsProps> = ({
  pledge,
}: PledgeDetailsProps) => {
  const { t } = useTranslation('my');

  return (
    <>
      <CommitmentDetailsSection
        title={t('my:commitments.category.financialPledges')}
      >
        {pledge?.pledgeName && (
          <>
            <PledgeIcon pledgeName={pledge.pledgeName} />

            <span className="label-lg label-potent label-neutral-potent">
              {formatPledgeName(pledge.pledgeName, t)}
            </span>
          </>
        )}

        <ul className="p-0 list-none m-0 CommitmentDetails__overview flex flex-col gap-snug-md">
          <CommitmentDetailsListItem
            title={t('my:commitments.pledges.details.totalAmount')}
          >
            {t('my:commitments.pledges.details.values.amount', {
              amount: pledge.totalPledgeAmount,
            })}
          </CommitmentDetailsListItem>
          <CommitmentDetailsListItem
            title={t('my:commitments.pledges.details.totalGiven')}
          >
            {t('my:commitments.pledges.details.values.amount', {
              amount: pledge.totalGivenToDate,
            })}
          </CommitmentDetailsListItem>
          <CommitmentDetailsListItem
            title={t('my:commitments.pledges.details.amountOutstanding')}
          >
            {t('my:commitments.pledges.details.values.amount', {
              amount: pledge.outstandingAmount,
            })}
          </CommitmentDetailsListItem>
        </ul>
      </CommitmentDetailsSection>

      {pledge?.lastInstallmentDate && pledge?.lastInstallmentAmount ? (
        <CommitmentDetailsSection
          title={t('my:commitments.pledges.details.lastInstallment')}
        >
          <ul className="p-0 list-none m-0 CommitmentDetails__overview flex flex-col gap-snug-md">
            <CommitmentDetailsListItem
              title={t('my:commitments.pledges.details.installmentDate')}
            >
              {t('my:commitments.pledges.details.values.installmentDate', {
                date: pledge?.lastInstallmentDate,
              })}
            </CommitmentDetailsListItem>
            <CommitmentDetailsListItem
              title={t('my:commitments.pledges.details.amount')}
            >
              {t('my:commitments.pledges.details.values.amount', {
                amount: pledge?.lastInstallmentAmount,
              })}
            </CommitmentDetailsListItem>
          </ul>
        </CommitmentDetailsSection>
      ) : null}

      {pledge?.nextInstallmentDate && pledge?.nextInstallmentAmount ? (
        <CommitmentDetailsSection
          title={t('my:commitments.pledges.details.nextInstallment')}
        >
          <ul className="p-0 list-none m-0 CommitmentDetails__overview flex flex-col gap-snug-md">
            <CommitmentDetailsListItem
              title={t('my:commitments.pledges.details.installmentDate')}
            >
              {t('my:commitments.pledges.details.values.installmentDate', {
                date: pledge?.nextInstallmentDate,
              })}
            </CommitmentDetailsListItem>
            <CommitmentDetailsListItem
              title={t('my:commitments.pledges.details.amount')}
            >
              {t('my:commitments.pledges.details.values.amount', {
                amount: pledge?.nextInstallmentAmount,
              })}
            </CommitmentDetailsListItem>
          </ul>
        </CommitmentDetailsSection>
      ) : null}
    </>
  );
};

export const CommitmentDetails: React.FC<CommitmentDetailsProps> = ({
  commitment,
}: CommitmentDetailsProps) => {
  const { t } = useTranslation(['my', 'core']);
  const { alerts, removeAlert } = useLocalAlerts();
  const [updatedCommitments, setUpdatedCommitments] = useState<
    GraphQLCommitment[]
  >([]);
  return (
    <>
      {alerts.length > 0 ? (
        <div>
          {alerts.map((alert) => (
            <LocalAlert
              key={alert.id}
              alert={alert}
              className="mb-comfy-sm"
              onClose={removeAlert}
            />
          ))}
        </div>
      ) : undefined}
      <MakeOneTimePaymentAlert
        updatedCommitments={updatedCommitments}
        presentationContext="commitmentDetails"
      />
      <CommitmentDetailsSection title={getCommitmentLabel(commitment, t)}>
        <CommitmentAvatar size="lg" commitment={commitment} />
        <span className="label-lg label-potent label-neutral-potent">
          {getCommitmentTitle(commitment, t)}
        </span>

        {/* TODO: Add alert here for assigning payment method to commitment. */}

        <ul className="p-0 list-none m-0 CommitmentDetails__overview flex flex-col gap-snug-md">
          {commitment.commitmentType ===
            GraphQLCommitmentType.ChildSponsorship && (
            <CommitmentDetailsListItem
              title={t('my:commitments.details.overview.list.id')}
            >
              {commitment.participant?.localId}
            </CommitmentDetailsListItem>
          )}
          <CommitmentDetailsListItem
            title={t('my:commitments.details.overview.list.amount')}
          >
            {t('core:numbers.currencyCustom', {
              amount: commitment.rate,
              useNegativeParens: true,
            })}
            <span>{getFrequencyAbbr(commitment.frequency || '', t)}</span>
          </CommitmentDetailsListItem>
          {(commitment.creditRemaining ?? 0) > 0 && (
            <CommitmentDetailsListItem
              title={t('my:commitments.details.overview.list.credit')}
            >
              {t('core:numbers.currencyCustom', {
                amount: commitment.creditRemaining,
              })}
            </CommitmentDetailsListItem>
          )}
          {!!commitment.isRecurring &&
            (commitment.paymentDateNext || commitment.paymentDatePending) && (
              <CommitmentDetailsListItem
                title={t('my:commitments.details.overview.list.nextPayment')}
              >
                {commitment.paymentDateNext
                  ? t('my:commitments.details.overview.values.nextPayment', {
                      date: commitment.paymentDateNext,
                    })
                  : t('my:commitments.statuses.pending')}
              </CommitmentDetailsListItem>
            )}
        </ul>
      </CommitmentDetailsSection>

      <CommitmentDetailsSection
        title={t('my:commitments.details.paymentMethod.title')}
      >
        <CommitmentDetailsPaymentPlan
          commitment={commitment}
          onCommitmentsUpdated={setUpdatedCommitments}
        />
      </CommitmentDetailsSection>

      {commitment.paymentPullDay && (
        <CommitmentDetailsSection
          title={t('my:commitments.details.paymentDay.title')}
        >
          <span className="label-moderate label-md label-neutral-mild">
            {t('my:commitments.details.paymentDay.paymentPullDayLabel', {
              count: commitment.paymentPullDay,
              ordinal: true,
            })}
          </span>
        </CommitmentDetailsSection>
      )}

      <CommitmentDetailsActionCard />
    </>
  );
};

export const CommitmentDetailsSheet: React.FC<
  React.PropsWithChildren<CommitmentDetailsSheetProps>
> = ({ isOpen = false, openTrigger, commitment, pledge, onOpenChange }) => {
  const { t } = useTranslation('my');
  const title = t('my:commitments.detailsSheet.heading');
  const { alerts: sheetAlerts, removeAlert: removeSheetAlert } =
    useModalAlerts();
  if (!commitment && !pledge) {
    return null;
  }

  return (
    <Sheet
      {...(isOpen !== undefined && { open: isOpen })}
      {...(onOpenChange &&
        commitment && {
          onOpenChange: (open) => {
            onOpenChange(open, commitment.internalId);
          },
        })}
      {...(onOpenChange &&
        pledge?.pledgeId && {
          onOpenChange: (open) => {
            onOpenChange(open, pledge.pledgeId!);
          },
        })}
    >
      <SheetTrigger>{openTrigger}</SheetTrigger>
      <SheetContent title={title}>
        <SheetHeader>{title}</SheetHeader>
        <SheetBody>
          <LocalAlertsProvider>
            {sheetAlerts.length > 0 && (
              <div className="mt-comfy-sm md:mt-comfy-lg">
                {sheetAlerts.map((sheetAlert) => (
                  <LocalAlert
                    key={sheetAlert.id}
                    alert={sheetAlert}
                    className="my-comfy-xs"
                    onClose={removeSheetAlert}
                  />
                ))}
              </div>
            )}

            <CommitmentDetailsBody
              title={
                commitment
                  ? t('my:commitments.details.subTitle')
                  : t('my:commitments.pledges.details.subTitle', {
                      name: pledge?.pledgeName,
                    })
              }
            >
              {commitment && (
                <CommitmentDetails
                  key={commitment.internalId}
                  commitment={commitment}
                />
              )}
              {pledge && <PledgeDetails pledge={pledge} />}
            </CommitmentDetailsBody>
          </LocalAlertsProvider>
        </SheetBody>
      </SheetContent>
    </Sheet>
  );
};
